import { useQuery, useQueryClient } from "react-query";
import { PersistenceAPI, PersistenceThread } from "../functions/requests/persistence";
import { useOidcAccessToken } from "@axa-fr/react-oidc";
import { useOidcConfigurationName } from "./useOidcConfigurationName";
import IModel from "../types/interfaces/IModel";
import { useState, useCallback } from "react";

export const useConversationListQuery = () => {
  const { oidcConfigurationName } = useOidcConfigurationName();
  const oidcAccessToken = useOidcAccessToken(oidcConfigurationName);
  const queryOptions = {
    staleTime: 30000, // 30 seconds
    cacheTime: 60000, // 1 minute
    enabled: true,
  };
  return useQuery(
    ["conversationList"],
    () => PersistenceAPI.getThreads(oidcAccessToken.accessToken),
    queryOptions,
  );
};

export const usePaginatedConversationList = (initialLimit: number = 50) => {
  const { oidcConfigurationName } = useOidcConfigurationName();
  const oidcAccessToken = useOidcAccessToken(oidcConfigurationName);
  const queryClient = useQueryClient();
  const [limit] = useState<number>(initialLimit);
  const [offset, setOffset] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [allThreads, setAllThreads] = useState<PersistenceThread[]>([]);
  const [refreshCounter, setRefreshCounter] = useState<number>(0);

  const query = useQuery(
    ["paginatedConversationList", offset, limit, refreshCounter],
    async () => {
      const threads = await PersistenceAPI.getThreads(
        oidcAccessToken.accessToken,
        limit,
        offset
      );
      return threads;
    },
    {
      staleTime: 30000, // 30 seconds
      cacheTime: 60000, // 1 minute
      enabled: true,
      onSuccess: (data) => {
        if (data.length < limit) {
          setHasMore(false);
        }

        setAllThreads(prevThreads => {
          const threadMap = new Map(prevThreads.map(thread => [thread.id, thread]));
          
          data.forEach(thread => {
            if (thread.id) {
              threadMap.set(thread.id, thread);
            }
          });
          
          return Array.from(threadMap.values());
        });
      }
    }
  );

  const updateThread = useCallback((updatedThread: PersistenceThread) => {
    if (!updatedThread.id) return;
    
    console.log("Updating thread in state:", {
      id: updatedThread.id,
      model: updatedThread.model,
      name: updatedThread.name
    });
    
    setAllThreads(prevThreads => {
      const threadMap = new Map(prevThreads.map(thread => [thread.id, thread]));
      
      // First log the current thread if it exists
      const existingThread = threadMap.get(updatedThread.id);
      if (existingThread) {
        console.log("Replacing thread:", {
          id: existingThread.id,
          model: existingThread.model,
          name: existingThread.name,
          newName: updatedThread.name,
          nameChanged: existingThread.name !== updatedThread.name
        });
      } else {
        console.log("Adding new thread to state");
      }
      
      // Then update it in the map
      threadMap.set(updatedThread.id, {...updatedThread});
      
      // Create a new array from the map values to ensure state update
      const updatedThreads = Array.from(threadMap.values());
      
      // Sort by updated_at to keep most recent threads at the top
      return updatedThreads.sort((a, b) => {
        // If updated_at exists on both, use it for sorting
        if (a.updated_at && b.updated_at) {
          return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
        }
        // If created_at exists on both, use it for sorting
        if (a.created_at && b.created_at) {
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        }
        // Default to keeping current order
        return 0;
      });
    });
    
    // Force a refetch after a short delay to ensure UI is in sync with backend
    setTimeout(() => {
      console.log("Forcing conversation list refresh to sync with backend");
      queryClient.invalidateQueries({ queryKey: ["paginatedConversationList"] });
    }, 1000);
  }, [queryClient]);

  const loadMore = useCallback(() => {
    if (hasMore && !query.isFetching) {
      setOffset(prevOffset => prevOffset + limit);
    }
  }, [hasMore, limit, query.isFetching]);

  const addThread = useCallback((newThread: PersistenceThread) => {
    if (!newThread.id) return;
    
    setAllThreads(prevThreads => {
      const exists = prevThreads.some(thread => thread.id === newThread.id);
      if (exists) return prevThreads;
      
      return [newThread, ...prevThreads];
    });
  }, []);

  const forceRefresh = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: ["paginatedConversationList"],
      refetchActive: true
    });
    
    setRefreshCounter(count => count + 1);
    
    if (offset > 0) {
      setOffset(0);
    }
    
    const freshThreads = await PersistenceAPI.getThreads(
      oidcAccessToken.accessToken,
      limit,
      0
    );
    
    setAllThreads(freshThreads);
    
    return freshThreads;
  }, [queryClient, offset, limit, oidcAccessToken.accessToken]);

  return {
    threads: allThreads,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    hasMore,
    loadMore,
    error: query.error,
    updateThread,
    addThread,
    forceRefresh,
    refetch: query.refetch
  };
};

export const useMessageListQuery = (threadId: string | null | undefined) => {
  const { oidcConfigurationName } = useOidcConfigurationName();
  const oidcAccessToken = useOidcAccessToken(oidcConfigurationName);
  const queryOptions = {
    staleTime: 30000, // 30 seconds
    cacheTime: 60000, // 1 minute
    enabled: true,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  };
  return useQuery(
    ["messageList", threadId],
    () =>
      threadId != null
        ? PersistenceAPI.getMessagesInThread(
            oidcAccessToken.accessToken,
            threadId,
          )
        : Promise.resolve([]),
    queryOptions,
  );
};

export const useChatMessageListQuery = (
  threadId: string | null | undefined,
  modelList: IModel[],
) => {
  const { oidcConfigurationName } = useOidcConfigurationName();
  const oidcAccessToken = useOidcAccessToken(oidcConfigurationName);
  const queryOptions = {
    staleTime: 30000, // 30 seconds
    cacheTime: 60000, // 1 minute
    enabled: true,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  };
  return useQuery(
    ["chatMessageList", threadId],
    () =>
      threadId != null
        ? PersistenceAPI.getMessagesInThreadAsChatMessages(
            oidcAccessToken.accessToken,
            threadId,
            modelList,
          )
        : Promise.resolve([]),
    queryOptions,
  );
};
